import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Badge, Button, Spinner, Form, Pagination } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { adminService } from '../../services/adminService';
import toast from 'react-hot-toast';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

const TYPE_FILTERS = [
  { value: 'all', label: 'All Types' },
  { value: 'refund_request', label: 'Refund Requests' },
  { value: 'payment_failed', label: 'Failed Payments' },
  { value: 'server_error', label: 'Server Errors' },
  { value: 'movie_approval', label: 'Movie Approvals' },
  { value: 'settings_change', label: 'Settings' },
  { value: 'booking_fee_tier_change', label: 'Booking Fee Tiers' },
];

const TYPE_META: Record<string, { icon: string; color: string }> = {
  refund_request: { icon: 'bi-arrow-counterclockwise', color: 'warning' },
  payment_failed: { icon: 'bi-exclamation-triangle', color: 'danger' },
  server_error: { icon: 'bi-bug', color: 'danger' },
  movie_approval: { icon: 'bi-film', color: 'info' },
  settings_change: { icon: 'bi-gear', color: 'secondary' },
  booking_fee_tier_change: { icon: 'bi-cash-coin', color: 'secondary' },
};

const NotificationCenter: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminService.getAdminNotifications({
        type: typeFilter === 'all' ? undefined : typeFilter,
        unreadOnly: unreadOnly ? 'true' : 'false',
        page,
        limit,
      });
      if (res.success && res.data) {
        setNotifications(res.data);
        if (res.pagination) {
          setTotalPages(res.pagination.totalPages || 1);
          setTotal(res.pagination.total || 0);
        }
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, unreadOnly, page]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleMarkRead = async (id: string) => {
    try {
      await adminService.markNotificationRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n)),
      );
    } catch {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await adminService.markAllNotificationsRead();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, isRead: true, readAt: new Date().toISOString() })),
      );
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Failed');
    }
  };

  const formatDateTime = (iso: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      <Helmet>
        <title>Notification Center - MyCinemas Admin</title>
      </Helmet>

      <div className="fade-in">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Notification Center</h2>
            <p className="text-muted mb-0">
              {total} total notification{total === 1 ? '' : 's'}
            </p>
          </div>
          <Button
            variant="outline-primary"
            onClick={handleMarkAllRead}
            disabled={!notifications.some((n) => !n.isRead)}
          >
            <i className="bi-check2-all me-1"></i>
            Mark all as read
          </Button>
        </div>

        <Card className="mb-3">
          <Card.Body>
            <div className="d-flex gap-3 align-items-center flex-wrap">
              <Form.Group>
                <Form.Label className="mb-1 small text-muted">Type</Form.Label>
                <Form.Select
                  size="sm"
                  value={typeFilter}
                  onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                  style={{ minWidth: 200 }}
                >
                  {TYPE_FILTERS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label className="mb-1 small text-muted">Filter</Form.Label>
                <Form.Check
                  type="switch"
                  id="unread-only"
                  label="Unread only"
                  checked={unreadOnly}
                  onChange={(e) => { setUnreadOnly(e.target.checked); setPage(1); }}
                />
              </Form.Group>

              <div className="ms-auto small text-muted">
                Page {page} of {totalPages}
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center text-muted py-5">
                <i className="bi-bell-slash fs-1 opacity-25 d-block mb-2"></i>
                <p className="mb-0">No notifications match the current filters</p>
              </div>
            ) : (
              <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 50 }}></th>
                    <th>Title</th>
                    <th>Message</th>
                    <th>Type</th>
                    <th>Created</th>
                    <th style={{ width: 100 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {notifications.map((n) => {
                    const meta = TYPE_META[n.type] || {
                      icon: 'bi-info-circle',
                      color: 'info',
                    };
                    return (
                      <tr
                        key={n.id}
                        style={{
                          background: !n.isRead ? '#f0f7ff' : undefined,
                          fontWeight: !n.isRead ? 500 : 400,
                        }}
                      >
                        <td>
                          <i
                            className={`bi ${meta.icon} text-${meta.color}`}
                            style={{ fontSize: '1.2rem' }}
                          ></i>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {n.title}
                            {!n.isRead && (
                              <Badge bg="primary" pill style={{ fontSize: '0.6rem' }}>
                                NEW
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td>
                          <small className="text-muted">{n.message}</small>
                        </td>
                        <td>
                          <Badge bg={meta.color}>
                            {(TYPE_FILTERS.find((t) => t.value === n.type) || { label: n.type }).label}
                          </Badge>
                        </td>
                        <td>
                          <small className="text-muted">{formatDateTime(n.createdAt)}</small>
                        </td>
                        <td>
                          {!n.isRead ? (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleMarkRead(n.id)}
                            >
                              <i className="bi-check2"></i>
                            </Button>
                          ) : (
                            <small className="text-muted">
                              <i className="bi-check2-all"></i> read
                            </small>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <Pagination>
              <Pagination.First disabled={page === 1} onClick={() => setPage(1)} />
              <Pagination.Prev disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />
              <Pagination.Item active>{page}</Pagination.Item>
              <Pagination.Next
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              />
              <Pagination.Last disabled={page === totalPages} onClick={() => setPage(totalPages)} />
            </Pagination>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationCenter;
